import React, { useState, useEffect } from 'react';
import { BarChart3, ShieldAlert, Activity, CheckCircle, AlertTriangle, Calendar, Building } from 'lucide-react';
import axios from 'axios';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area,
  BarChart, Bar, Legend, PieChart, Pie, Cell
} from 'recharts';

const AnalyticsDashboard = ({ inline = false }) => {
  const [incidents, setIncidents] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeProjectName = localStorage.getItem('activeProjectName') || 'Proyecto Sin Asignar';

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        const [incidentsRes, photosRes] = await Promise.all([
          axios.get('http://localhost:4000/api/incidents'),
          axios.get('http://localhost:4000/api/photos')
        ]);
        
        if (incidentsRes.data && Array.isArray(incidentsRes.data)) {
          setIncidents(incidentsRes.data);
        }
        if (photosRes.data && Array.isArray(photosRes.data)) {
          setPhotos(photosRes.data);
        }
      } catch (error) {
        console.error("Error loading analytics data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  if (loading) {
    return (
      <div className={inline ? "py-10 text-center" : "min-h-screen bg-theme-bg flex items-center justify-center text-theme-text-primary"}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-xl font-light">Procesando analíticas de obra...</p>
        </div>
      </div>
    );
  }

  // 1. Calcular Métricas de Seguridad
  const totalIncidents = incidents.length;
  const resolvedIncidents = incidents.filter(i => i.is_resolved).length;
  const activeIncidents = totalIncidents - resolvedIncidents;
  const safetyCompliance = totalIncidents > 0 
    ? Math.round(((resolvedIncidents) / totalIncidents) * 100) 
    : 100;

  // 2. Incidentes por Categoría
  const typeCounts = incidents.reduce((acc, current) => {
    acc[current.incident_type] = (acc[current.incident_type] || 0) + 1;
    return acc;
  }, {});

  const typeData = Object.keys(typeCounts).map(type => ({
    name: type,
    value: typeCounts[type]
  }));

  // 3. Frecuencia de incidentes por día (Últimos 7 días)
  const last7DaysData = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    
    // Contar incidentes en este día
    const count = incidents.filter(incident => {
      const incidentDate = new Date(incident.created_at);
      return incidentDate.toDateString() === d.toDateString();
    }).length;

    return { day: dateStr, incidentes: count, rawDate: d };
  }).reverse();

  // 4. Conteo Histórico de Operarios en Obra
  const operatorTrend = photos.slice().reverse().map(p => {
    const dateObj = new Date(p.captured_at);
    const timeLabel = `${String(dateObj.getHours()).padStart(2, '0')}:${String(dateObj.getMinutes()).padStart(2, '0')}`;
    return {
      hora: timeLabel,
      operarios: p.person_count || 0
    };
  });

  const COLORS = ['#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#10b981'];

  return (
    <div className={inline ? "w-full" : "min-h-screen bg-theme-bg bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(16,185,129,0.1),rgba(255,255,255,0))] text-theme-text-primary p-4 md:p-8 font-sans pb-20"}>
      
      {/* Header */}
      {!inline && (
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight text-theme-text-primary mb-2 flex items-center gap-3">
              <BarChart3 className="text-emerald-500 drop-shadow-md" size={36} /> Analíticas de Obra <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-500">AI</span>
            </h1>
            <p className="text-theme-text-secondary flex items-center gap-2 font-medium">
              <Building size={18} className="text-emerald-400" /> Monitoreo y cumplimiento normativo <span className="text-slate-600 px-2">•</span> {activeProjectName}
            </p>
          </div>
        </div>
      )}

      {/* Grid de KPIs */}
      {!inline && (
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          
          {/* KPI: Compliance Rate */}
          <div className="bg-theme-panel/80 backdrop-blur-xl p-6 rounded-3xl border border-theme-border flex flex-col justify-between shadow-2xl relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest">Índice EHS</span>
              <div className="bg-emerald-500/10 p-2 rounded-xl border border-emerald-500/20 text-emerald-400">
                <CheckCircle size={20} />
              </div>
            </div>
            <div>
              <h3 className="text-4xl font-black text-emerald-400">{safetyCompliance}%</h3>
              <p className="text-xs text-theme-text-secondary mt-1">Cumplimiento acumulado de alertas</p>
            </div>
          </div>

          {/* KPI: Active Incidents */}
          <div className="bg-theme-panel/80 backdrop-blur-xl p-6 rounded-3xl border border-theme-border flex flex-col justify-between shadow-2xl relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest">Alertas Activas</span>
              <div className="bg-red-500/10 p-2 rounded-xl border border-red-500/20 text-red-400">
                <ShieldAlert size={20} />
              </div>
            </div>
            <div>
              <h3 className="text-4xl font-black text-red-400">{activeIncidents}</h3>
              <p className="text-xs text-theme-text-secondary mt-1">Acciones correctivas requeridas</p>
            </div>
          </div>

          {/* KPI: Resolved Incidents */}
          <div className="bg-theme-panel/80 backdrop-blur-xl p-6 rounded-3xl border border-theme-border flex flex-col justify-between shadow-2xl relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest">Resueltos</span>
              <div className="bg-blue-500/10 p-2 rounded-xl border border-blue-500/20 text-blue-400">
                <CheckCircle size={20} />
              </div>
            </div>
            <div>
              <h3 className="text-4xl font-black text-blue-400">{resolvedIncidents}</h3>
              <p className="text-xs text-theme-text-secondary mt-1">Infracciones mitigadas exitosamente</p>
            </div>
          </div>

          {/* KPI: Total Telemetry Logs */}
          <div className="bg-theme-panel/80 backdrop-blur-xl p-6 rounded-3xl border border-theme-border flex flex-col justify-between shadow-2xl relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest">Total Capturas</span>
              <div className="bg-purple-500/10 p-2 rounded-xl border border-purple-500/20 text-purple-400">
                <Calendar size={20} />
              </div>
            </div>
            <div>
              <h3 className="text-4xl font-black text-purple-400">{photos.length}</h3>
              <p className="text-xs text-theme-text-secondary mt-1">Fotografías y telemetry checks registrados</p>
            </div>
          </div>

        </div>
      )}

      {/* Grid de Gráficos */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Gráfico 1: Historial de Alertas (Últimos 7 días) */}
        <div className="lg:col-span-2 bg-theme-panel/80 border border-theme-border backdrop-blur-xl p-6 rounded-3xl shadow-2xl">
          <h3 className="text-lg font-bold mb-6 text-theme-text-primary flex items-center gap-2">
            <Activity size={18} className="text-emerald-400" /> Frecuencia de Alertas de Seguridad
          </h3>
          <div className="h-[320px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={last7DaysData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorIncidents" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff0a" vertical={false} />
                <XAxis dataKey="day" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.9)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', color: '#fff' }}
                  itemStyle={{ color: '#34d399', fontWeight: 'bold' }}
                />
                <Area type="monotone" dataKey="incidentes" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorIncidents)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráfico 2: Infracciones por Tipo (Pie Chart) */}
        <div className="bg-theme-panel/80 border border-theme-border backdrop-blur-xl p-6 rounded-3xl shadow-2xl flex flex-col">
          <h3 className="text-lg font-bold mb-6 text-theme-text-primary flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-400" /> Distribución de Infracciones
          </h3>
          <div className="flex-1 flex items-center justify-center min-h-[220px]">
            {typeData.length === 0 ? (
              <p className="text-sm text-theme-text-secondary">Sin datos de incidentes registrados</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={typeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {typeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.9)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', color: '#fff' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          {/* Leyenda Personalizada */}
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            {typeData.map((entry, index) => (
              <div key={entry.name} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                <span className="truncate text-theme-text-secondary">{entry.name} ({entry.value})</span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Gráfico 3: Tendencia de Operarios en Obra */}
      {operatorTrend.length > 0 && (
        <div className="max-w-7xl mx-auto mt-8 bg-theme-panel/80 border border-theme-border backdrop-blur-xl p-6 rounded-3xl shadow-2xl">
          <h3 className="text-lg font-bold mb-6 text-theme-text-primary flex items-center gap-2">
            <Activity size={18} className="text-blue-400" /> Curva de Presencia de Operarios (Vision AI)
          </h3>
          <div className="h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={operatorTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff0a" vertical={false} />
                <XAxis dataKey="hora" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.9)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', color: '#fff' }}
                  itemStyle={{ color: '#60a5fa', fontWeight: 'bold' }}
                />
                <Bar dataKey="operarios" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

    </div>
  );
};

export default AnalyticsDashboard;
